import type { supabase as SupabaseClient } from "@/integrations/supabase/client";

let cached: Promise<typeof SupabaseClient> | null = null;
const waiting: ((client: typeof SupabaseClient) => void)[] = [];

/** Sant när databasbiblioteket redan hämtats av någon del av sidan. */
export function isSupabaseLoaded() {
  return cached !== null;
}

/**
 * Kör återanropet så snart databasklienten laddats – nu eller senare. Används
 * av inloggningslyssnaren, som annars skulle tvinga fram hämtningen direkt.
 */
export function whenSupabaseLoaded(cb: (client: typeof SupabaseClient) => void) {
  if (cached) void cached.then(cb);
  else waiting.push(cb);
}

/** Sant om webbläsaren har en sparad inloggning – då behövs klienten direkt. */
export function hasStoredSession() {
  if (typeof window === "undefined") return false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && k.includes("auth-token")) return true;
    }
  } catch {
    /* blockerad lagring */
  }
  try {
    if (/sb-[^=;]*auth-token/.test(document.cookie)) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Hämtar databasklienten först när den behövs. Det håller startpaketet litet
 * eftersom biblioteket (~200 kB) annars laddas innan sidan hunnit visas.
 */
export function getSupabase(): Promise<typeof SupabaseClient> {
  if (!cached) {
    cached = import("@/integrations/supabase/client").then((m) => {
      while (waiting.length) waiting.shift()!(m.supabase);
      return m.supabase;
    });
  }
  return cached;
}

type AuthChangeCallback = (event: string, session: any) => void | Promise<void>;

/**
 * Samma auth-anrop som vanligt, men klienten hämtas i bakgrunden. Används på
 * sidor som annars skulle tvinga in hela databasbiblioteket i startpaketet.
 */
export const lazyAuth = {
  getSession: async () => (await getSupabase()).auth.getSession(),
  getUser: async () => (await getSupabase()).auth.getUser(),
  setSession: async (args: { access_token: string; refresh_token: string }) =>
    (await getSupabase()).auth.setSession(args),
  updateUser: async (args: { password?: string; email?: string }) =>
    (await getSupabase()).auth.updateUser(args),
  onAuthStateChange: (cb: AuthChangeCallback) => {
    let stop: (() => void) | undefined;
    let dead = false;
    void getSupabase().then((sb) => {
      if (dead) return;
      stop = sb.auth.onAuthStateChange(cb as never).data.subscription.unsubscribe;
    });
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            dead = true;
            stop?.();
          },
        },
      },
    };
  },
};
